import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { useAuth } from '../auth/useAuth';

/**
 * For an authenticated user landing on `/join/:code`, the next-step path:
 * the consolidated ProfileSetup step (spec 026). Exported for unit testing.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getNextStep(code: string): string {
  return `/join/${code}/profile`;
}

interface WelcomeGuardProps {
  children: React.ReactNode;
}

const WelcomeGuard: React.FC<WelcomeGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { code } = useParams<{ code?: string }>();
  const location = useLocation();

  // While auth state is still being resolved, don't redirect — let the
  // children render so a brief flicker resolves into the correct view once
  // useAuth settles.
  if (isLoading) return <>{children}</>;

  if (isAuthenticated) {
    if (code) {
      // Spec 025 FR-007a — skip Welcome for logged-in users joining a run.
      // Preserve the search query so deep-link params like `?team=<code>`
      // (used by the QR-share flow) propagate through the redirect chain.
      return <Navigate to={{ pathname: getNextStep(code), search: location.search }} replace />;
    }
    // Spec 025 FR-007 — logged-in users on `/` go straight to the dashboard.
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default WelcomeGuard;
